import { SelectProps } from 'antd'
import { useFilter } from 'shared/common/OpenFiltersButton/hooks/useFilter'
import { Icon } from 'shared/iconpack'
import { useAppSearchParams } from 'shared/libs/hooks/useAppSearchParams'
import { FormQuery, ParamKey } from 'shared/types/QueryModel'

import { UIEvent, useEffect, useMemo, useState } from 'react'

import SelectWithCheckbox from 'shared/ui/Form/SelectWithCheckbox'

import { palette } from 'shared/config/styles/palette'

interface SelectQueryProps extends FormQuery {
  options: SelectProps['options']
  removeKeys?: ParamKey[]
  isLoading?: boolean
  onScrollEnd?: () => Promise<void>
}
export function SelectQuery({
  queryKey,
  defaultValue,
  className,
  placeholder,
  removeKeys,
  isLoading = false,
  options,
  onScrollEnd,
  ...props
}: SelectQueryProps & SelectProps) {
  const { filters, setFilters, deleteFilter } = useFilter()
  const { deleteParam } = useAppSearchParams({
    removeKeys: removeKeys,
  })

  const [isScrollEnd, setIsScrollEnd] = useState<boolean>(false)
  const value = filters?.[queryKey]?.split(',').filter(Boolean)
  const validOptions = useMemo(
    () => options?.map((opt) => opt.value) ?? null,
    [options],
  )

  const onChange = (values: string | string[]) => {
    if (!values) {
      onClear()
      return
    }
    const filterValue = Array.isArray(values) ? values.join(',') : values
    if (filterValue) {
      setFilters([
        {
          [queryKey]: filterValue,
        },
      ])
    } else {
      deleteFilter(queryKey)
      deleteParam(queryKey)
    }
  }

  const handlePopupScroll = (e: UIEvent<HTMLDivElement>) => {
    if (!onScrollEnd) return
    const { target } = e
    if (
      !isScrollEnd &&
      (target as any).scrollTop + (target as any).offsetHeight >
        (target as any).scrollHeight - 100
    ) {
      setIsScrollEnd(true)
    }
  }

  const onClear = () => {
    deleteParam(queryKey)
  }

  useEffect(() => {
    if (validOptions && validOptions.length > 0 && Array.isArray(value)) {
      const filteredValue = value
        .filter((v) => validOptions.includes(v))
        .join(',')
      if (filteredValue !== filters?.[queryKey]) {
        setFilters([{ [queryKey]: filteredValue }])
      }
    }
  }, [validOptions])

  useEffect(() => {
    if (isScrollEnd && onScrollEnd) {
      const handleOnScrollEnd = async () => {
        await onScrollEnd()
        setIsScrollEnd(false)
      }

      handleOnScrollEnd()
    }
  }, [isScrollEnd])
  return (
    <SelectWithCheckbox
      allowClear={{
        clearIcon: (
          <Icon
            name="close"
            width={25}
            height={24}
            color={palette.dark_blue_1}
          />
        ),
      }}
      value={value ?? defaultValue}
      showSearch={false}
      onClear={onClear}
      options={options}
      onChange={onChange}
      onPopupScroll={handlePopupScroll}
      placeholder={placeholder}
      className={className}
      isLoading={isLoading}
      {...props}
    />
  )
}
